import { cn } from "@/lib/utils";
import { Skeleton } from "./ui/skeleton";

interface SkeletonLineProps {
  width?: string;
  height?: string;
  className?: string;
}

export function SkeletonLine({
  width = "w-full",
  height = "h-4",
  className = "",
}: SkeletonLineProps) {
  return <Skeleton className={cn(height, width, className)} />;
}

interface SkeletonCardProps {
  lines?: number;
  className?: string;
}

export function SkeletonCard({ lines = 3, className = "" }: SkeletonCardProps) {
  return (
    <div
      className={cn(
        "bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border-default)] p-6",
        className,
      )}
    >
      <Skeleton className="h-5 w-48 mb-3" />
      {Array.from({ length: lines - 1 }, (_, i) => (
        <Skeleton key={i} className={cn("h-4 mb-2", i === lines - 2 ? "w-40" : "w-72")} />
      ))}
    </div>
  );
}

/**
 * Full-screen centered loading card used by auth/admin route gates while
 * session or role is being resolved. Kept consistent across gates so the
 * loading identity is the same before the app shell renders.
 */
export function GateLoadingScreen() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900"
      aria-busy="true"
      aria-label="Checking access"
    >
      <div className="w-full max-w-md px-4">
        <SkeletonCard lines={2} />
      </div>
    </div>
  );
}

/**
 * Content-area skeleton used inside Layout while lazy route chunks load.
 * Matches the content region of the app shell so the nav stays visible.
 */
export function ContentSkeleton() {
  return (
    <div className="min-h-[50vh] space-y-6" aria-busy="true" aria-label="Loading page">
      <SkeletonLine width="w-48" height="h-6" />
      <div className="space-y-4">
        <SkeletonCard lines={3} />
        <SkeletonCard lines={2} />
      </div>
    </div>
  );
}

/**
 * Full-page skeleton matching the Layout structure (nav bar + content area).
 * Used by auth/beta gates so all loading stages share one visual identity,
 * eliminating the multi-stage stutter effect.
 */
export function AppShellSkeleton() {
  return (
    <div className="min-h-screen bg-[var(--color-bg-page)]">
      <div className="bg-[var(--color-bg-card)] shadow-sm border-b border-[var(--color-border-default)]">
        <div className="max-w-7xl 2xl:max-w-[100rem] mx-auto px-4 2xl:px-8">
          <div className="flex items-center justify-between h-16">
            <Skeleton className="h-5 w-28" />
            <div className="hidden lg:flex items-center gap-4">
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl 2xl:max-w-[100rem] mx-auto px-4 2xl:px-8 pt-4 sm:pt-6 pb-4 sm:pb-8">
        <ContentSkeleton />
      </div>
    </div>
  );
}
